// frontend/src/components/MonitorProject.js

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  IconButton,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../services/api';

const truncateId = (id) => {
  if (!id || id.length <= 12) return id;
  return `${id.slice(0, 5)}..${id.slice(-5)}`;
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return (
    <>
      <Typography variant="body2" component="span" display="block">
        {date.toLocaleDateString()}
      </Typography>
      <Typography 
        variant="body2" 
        component="span" 
        display="block" 
        color="text.secondary"
        sx={{ fontSize: '0.875em' }}
      >
        {date.toLocaleTimeString()}
      </Typography>
    </>
  );
};

function MonitorProject({ projectId }) {
  const [monitorData, setMonitorData] = useState(null);
  const [responseHistory, setResponseHistory] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/projects/${projectId}/monitor`),
      api.get(`/projects/${projectId}/responses`)
    ])
      .then(([monitorRes, responsesRes]) => {
        setMonitorData(monitorRes.data);
        const sortedResponses = responsesRes.data.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setResponseHistory(sortedResponses);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching monitor data:', error);
        setError('Failed to load monitoring data');
        setLoading(false);
      });
  }, [projectId]);

  const handleAnalyzedToggle = (responseId, newValue) => {
    api.put(`/projects/${projectId}/responses/${responseId}/analyzed`, {
      is_analyzed: newValue
    })
    .then(() => {
      setResponseHistory(prevHistory => 
        prevHistory.map(r => 
          r.id === responseId 
            ? {...r, is_analyzed: newValue}
            : r
        )
      );
      api.get(`/projects/${projectId}/monitor`)
        .then((monitorRes) => {
          setMonitorData(monitorRes.data);
        })
        .catch(error => {
          console.error('Error refreshing monitor data:', error);
        });
    })
    .catch(error => {
      console.error('Error updating analyzed status:', error);
    });
  };

  const handleExpandRow = (index) => {
    setExpanded(prevExpanded => {
      const newExpanded = [...prevExpanded];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Statistics Cards - made more compact */}
      <Box sx={{ 
        display: 'grid', 
        gap: 1,
        mb: 3,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }
      }}>
        <Paper sx={{ 
          p: 2,
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}>
          <Typography variant="h4" color="primary" sx={{ mb: 0.5 }}>
            {monitorData.visits}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visits
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" color="primary" sx={{ mb: 0.5 }}>
            {monitorData.starts}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Starts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {monitorData.start_rate}% of visits
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" color="primary" sx={{ mb: 0.5 }}>
            {monitorData.completes}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completes
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {monitorData.completion_rate}% of starts
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" color="primary" sx={{ mb: 0.5 }}>
            {monitorData.analyzed}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analyzed
          </Typography>
        </Paper>
      </Box>

      {/* Response History Table */}
      <Paper sx={{ width: '100%' }}>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
          Response History
        </Typography>
        <Divider />
        
        {responseHistory.length > 0 ? (
          <TableContainer>
            <Table 
              size="small" 
              sx={{
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': {
                  borderBottom: 'none',
                },
                '& .MuiTableRow-root:not(.expanded-row)': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiTableHead-root .MuiTableRow-root': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '40px' }}>#</TableCell>
                  <TableCell sx={{ width: '100px' }}>Time</TableCell>
                  <TableCell sx={{ width: '180px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ width: '70px' }}>Analyzed</TableCell>
                  <TableCell align="center" sx={{ width: '40px' }}>View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responseHistory.map((response, index) => (
                  <React.Fragment key={index}>
                    <TableRow hover>
                      <TableCell>{responseHistory.length - index}</TableCell>
                      <TableCell>{formatDateTime(response.created_at)}</TableCell>
                      <TableCell>
                        {response.is_completed ? (
                          <Typography sx={{ color: 'success.main' }}>
                            Completed (answered all questions)
                          </Typography>
                        ) : (
                          <Typography sx={{ color: 'error.main' }}>
                            Incomplete (started interview)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={response.is_analyzed}
                          onChange={(e) => handleAnalyzedToggle(response.id, e.target.checked)}
                          disabled={response.is_completed}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(index)}
                          sx={{ transform: expanded[index] ? 'rotate(180deg)' : 'none' }}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow className="expanded-row">
                      <TableCell 
                        colSpan={5} 
                        sx={{ 
                          py: 0,
                          backgroundColor: 'white',
                        }}
                      >
                        <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ 
                              mb: 2,
                              backgroundColor: 'white',
                              p: 2,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: response.summary ? 2 : 0 }}>
                                <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>
                                  Client ID:
                                </Typography>
                                <Chip 
                                  label={response.customer_id}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: 'rgba(0, 123, 255, 0.1)',
                                    color: 'primary.main',
                                    fontFamily: 'monospace'
                                  }}
                                />
                              </Box>
                              {response.summary && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Summary:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {response.summary}
                                  </Typography>
                                </>
                              )}
                            </Box>
                            <Box component="ul" sx={{ 
                              m: 0, 
                              p: 0,
                              listStyle: 'none'
                            }}>
                              {response.answers.map((answer, answerIndex) => (
                                <Box component="li" key={answerIndex} sx={{ mb: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {answer.question_text}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {answer.answer_text || 'No answer provided'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No responses yet.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default MonitorProject;
